import { PatientsResponse, Patient } from "@features/clinic-patients/clinic/types";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { useSelector } from "react-redux";
import { RootState } from "@app/store/store";
import { maskNumber, maskCurrency, maskEmail, maskPhone, maskText } from "@shared/lib";

interface UsePatientParams {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  appliedFilters?: Record<string, any>;
  columns?: string[];
}

export default function usePatients(
  clinicId?: string,
  { appliedFilters = {}, columns = [] }: UsePatientParams = {
    appliedFilters: {},
    columns: [],
  }
) {
  const { GET } = useApiRequest();
  const shouldMask = useSelector(
    (state: RootState) => state.app.obfuscateSensitiveData
  );
  const sensitiveDataProperties = useSelector(
    (state: RootState) => state.app.sensitiveDataProperties
  );

  const getMaskedValue = (propertyName: string, value: unknown): string => {
    if (
      !shouldMask ||
      !sensitiveDataProperties.includes(propertyName) ||
      value == null
    ) {
      return value?.toString() ?? "";
    }

    if (
      propertyName === "payments_total_spent" ||
      propertyName === "payments_total_pending_payment"
    ) {
      return maskCurrency(value as string | number);
    } else if (
      propertyName === "core_visits_count" ||
      propertyName === "core_last_visit_at"
    ) {
      return maskNumber(value as string | number);
    } else if (propertyName === "core_email") {
      return maskEmail(value as string);
    } else if (propertyName === "core_phone") {
      return maskPhone(value as string);
    } else {
      return maskText(value as string);
    }
  };

  return useQuery({
    queryKey: [
      "patients",
      clinicId,
      appliedFilters,
      columns,
      sensitiveDataProperties,
      shouldMask,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();

      Object.entries(appliedFilters).forEach(([key, value]) => {
        if (
          value !== undefined &&
          value !== null &&
          value !== "" &&
          key !== "fields"
        ) {
          params.append(key, String(value));
        }
      });

      if (columns.length > 0) {
        params.append("fields", columns.join(","));
      }

      const response = await GET<PatientsResponse>(
        `/api/healthcare-companies/${clinicId}/patients?${params.toString()}`
      );

      if (shouldMask && response.patients) {
        return {
          ...response,
          patients: response.patients.map((patient: Patient) => ({
            ...patient,
            ...Object.fromEntries(
              Object.entries(patient).map(([key, value]) => [
                key,
                sensitiveDataProperties.includes(key)
                  ? getMaskedValue(key, value)
                  : value,
              ])
            ),
          })),
        };
      }

      return response;
    },
    enabled: Boolean(clinicId),
    placeholderData: keepPreviousData,
  });
}
